import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Bot, Send, X, MessageCircle, Loader2, CheckCircle, AlertCircle, RefreshCw, Copy, CopyCheck, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { locations } from "@/data/locations";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'ai-team';
  content: string;
  timestamp: Date;
  suggestion?: CampaignSuggestion;
  sender?: string;
  role?: string;
}

interface CampaignSuggestion {
  companyName?: string;
  brandName?: string;
  personInCharge?: string;
  email?: string;
  mobile?: string;
  jobTitle?: string;
  suggestedLocations: Array<{
    id: number;
    name: string;
    devices: number;
    mediaOwner: string;
    region: string;
    type: string;
    reasoning: string;
    dailyRate?: number;
    totalLocationCost?: number;
  }>;
  recommendedBudget: number;
  duration: number;
  campaignStrategy: string;
  targetAudience: string;
  campaignName: string;
  totalDailyCost?: number;
  costBreakdown?: {
    byMediaOwner?: Record<string, number>;
    byLocation?: Array<{
      name: string;
      dailyCost: number;
      devices: number;
      rate: number;
    }>;
  };
  budgetSummary?: string;
}

interface AIAssistantButtonProps {
  currentStep: number;
  formData: any;
  selectedLocations: any[];
  budget: number;
  onCampaignSuggestion: (suggestion: any) => void;
}

const AI_TEAM_MEMBERS = [
  { 
    name: 'Fusion AI Strategist', 
    nameAr: 'خبير الاستراتيجية فيوجن',
    role: 'strategist', 
    roleAr: 'استراتيجي',
    icon: '🎯' 
  },
  { 
    name: 'Fusion AI Data Analyst', 
    nameAr: 'محلل البيانات فيوجن',
    role: 'analyst', 
    roleAr: 'محلل',
    icon: '📊' 
  },
  { 
    name: 'Fusion AI Inventory', 
    nameAr: 'مخزون فيوجن',
    role: 'inventory', 
    roleAr: 'مخزون',
    icon: '📦' 
  },
  { 
    name: 'Fusion AI Planner', 
    nameAr: 'مخطط فيوجن',
    role: 'planner', 
    roleAr: 'مخطط',
    icon: '📋' 
  },
  { 
    name: 'Fusion AI Agency', 
    nameAr: 'وكالة فيوجن',
    role: 'agency', 
    roleAr: 'وكالة',
    icon: '🏢' 
  }
];

const AIAssistantButton = ({
  currentStep,
  formData,
  selectedLocations,
  budget,
  onCampaignSuggestion
}: AIAssistantButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      content: 'Hello! I\'m your AI Campaign Assistant. I can help you create a complete campaign plan using our network of 4,000+ Screens across TableAds, Alan, Waki, and Attention media houses. Tell me about your campaign goals, target audience, and budget to get started!',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState<CampaignSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [isTeamDiscussing, setIsTeamDiscussing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleCopyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      toast({
        title: "Copied!",
        description: "Message copied to clipboard",
        variant: "default"
      });
      
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleApplySuggestion = (suggestion: CampaignSuggestion) => {
    try {
      console.log('🔥 APPLYING AI SUGGESTION:', suggestion);
      console.log('🔥 SUGGESTED LOCATIONS:', suggestion.suggestedLocations);
      suggestion.suggestedLocations.forEach((loc, index) => {
        console.log(`🔥 Location ${index + 1}:`, {
          id: loc.id,
          name: loc.name,
          region: loc.region,
          mediaOwner: loc.mediaOwner,
          devices: loc.devices,
          reasoning: loc.reasoning
        });
      });
      
      onCampaignSuggestion(suggestion);
      
      localStorage.setItem('aiSuggestionApplied', 'true');
      localStorage.setItem('aiSuggestionData', JSON.stringify(suggestion));
      localStorage.setItem('aiSuggestionTimestamp', new Date().toISOString());
        
      toast({
        title: "Campaign Applied!",
        description: "AI suggestions have been applied to your campaign form.",
      });
      
      setCurrentSuggestion(null);
      setIsOpen(false);
    } catch (error) {
      console.error('Error applying suggestion:', error);
      toast({
        title: "Error",
        description: "Failed to apply AI suggestions. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getLocationStats = () => {
    const totalLocations = locations.length;
    const mediaOwners = [...new Set(locations.map(loc => loc.mediaOwner))];
    const regions = [...new Set(locations.map(loc => loc.region))];
    const types = [...new Set(locations.map(loc => loc.type))];
    
    return {
      totalLocations,
      mediaOwners,
      regions,
      types,
      indoorCount: locations.filter(loc => loc.mediaKind === 'Indoor').length,
      outdoorCount: locations.filter(loc => loc.mediaKind === 'Outdoor').length
    };
  };

  // Helper function to detect if text is Arabic
  const isArabicText = (text: string): boolean => {
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text);
  };

  const simulateTeamDiscussion = async (userMessage: string): Promise<void> => {
    setIsTeamDiscussing(true);
    
    const isArabic = isArabicText(userMessage);
    
    const discussions = isArabic ? [
      {
        sender: 'خبير الاستراتيجية فيوجن',
        role: 'استراتيجي',
        content: `لقد قمت بتحليل طلب الحملة: "${userMessage}". بناءً على السوق المستهدف وأهداف الحملة، أنصح بالتركيز على المواقع الاستراتيجية التي تتماشى مع الجمهور المستهدف.`,
        delay: 1500
      },
      {
        sender: 'محلل البيانات فيوجن',
        role: 'محلل',
        content: `بناءً على تحليل بيانات الأداء، أرى معدلات تفاعل قوية في المطاعم والمراكز التجارية. يجب أن يعطي توزيع الميزانية الأولوية للمواقع عالية الحركة.`,
        delay: 2000
      },
      {
        sender: 'وكالة فيوجن',
        role: 'وكالة',
        content: `ممتاز! تعاون فريقنا لإنشاء خطة وسائط شاملة. أقوم الآن بتجميع استراتيجية الحملة النهائية مع تفصيل مفصل للميزانية...`,
        delay: 1000
      }
    ] : [
      {
        sender: 'Fusion AI Strategist',
        role: 'strategist',
        content: `I've analyzed the campaign request: "${userMessage}". Looking at our target market and campaign objectives, I recommend focusing on strategic placement that aligns with the target demographic.`,
        delay: 1500
      },
      {
        sender: 'Fusion AI Data Analyst',
        role: 'analyst',
        content: `Based on performance data analysis, I'm seeing strong engagement rates in restaurants (TableAds) and fitness centers (Alan). Budget allocation should prioritize high-traffic locations with proven ROI.`,
        delay: 2000
      },
      {
        sender: 'Fusion AI Agency',
        role: 'agency',
        content: `Perfect! Our team has collaborated to create a comprehensive media plan. I'm now compiling the final campaign strategy with detailed budget breakdown and location selections...`,
        delay: 1000
      }
    ];

    for (const discussion of discussions) {
      await new Promise(resolve => setTimeout(resolve, discussion.delay));
      
      const discussionMessage: Message = {
        id: Date.now().toString() + Math.random(),
        type: 'ai-team',
        content: discussion.content,
        timestamp: new Date(),
        sender: discussion.sender,
        role: discussion.role
      };
      
      setMessages(prev => [...prev, discussionMessage]);
    }
    
    setIsTeamDiscussing(false);
  };

  const generateCampaignSuggestion = async (userMessage: string): Promise<CampaignSuggestion | null> => {
    try {
      const locationStats = getLocationStats();
      const isArabic = isArabicText(userMessage);
      
      console.log('Sending complete location data to AI:', {
        totalLocations: locations.length,
        mediaOwners: locationStats.mediaOwners,
        regions: locationStats.regions,
        language: isArabic ? 'Arabic' : 'English'
      });

      const prompt = isArabic ? 
        `أنت خبير استراتيجي في الحملات الإعلانية مع إمكانية الوصول إلى شبكة وسائط شاملة. قم بإنشاء خطة حملة مفصلة بناءً على طلب المستخدم: "${userMessage}"

الموارد المتاحة:
- إجمالي المواقع: ${locationStats.totalLocations}
- مالكو الوسائط: ${locationStats.mediaOwners.join('، ')}
- المناطق: ${locationStats.regions.join('، ')}

يرجى إنشاء اقتراح حملة شامل يتضمن المواقع الموصى بها والميزانية ومدة الحملة واستراتيجية الحملة.` :
        `You are an expert campaign strategist with access to a comprehensive media network. Create a detailed campaign plan based on the user's request: "${userMessage}"

Available Resources:
- Total Locations: ${locationStats.totalLocations}
- Media Owners: ${locationStats.mediaOwners.join(', ')}
- Regions: ${locationStats.regions.join(', ')}

Please create a comprehensive campaign suggestion including recommended locations, budget allocation, campaign duration, and strategy.`;

      console.log('Calling AI campaign planner...');
      const response = await supabase.functions.invoke('ai-campaign-planner', {
        body: { 
          prompt, 
          userMessage, 
          locationData: locations
        }
      });

      console.log('AI function response:', response);

      if (response.error) {
        console.error('AI response error:', response.error);
        throw new Error(response.error.message || 'AI service temporarily unavailable');
      }

      if (!response.data) {
        throw new Error('No data received from AI service');
      }

      return response.data?.suggestion || null;
    } catch (error) {
      console.error('Error generating campaign suggestion:', error);
      throw error;
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const sanitizedInput = inputValue.trim().replace(/[<>]/g, '').substring(0, 2000);
    if (!sanitizedInput) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid message",
        variant: "destructive"
      });
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: sanitizedInput,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Start team discussion simulation
      await simulateTeamDiscussion(userMessage.content);
      
      const suggestion = await generateCampaignSuggestion(userMessage.content);
      
      let assistantContent = '';
      let assistantMessage: Message;
      
      if (suggestion) {
        setRetryCount(0);
        const isArabic = isArabicText(userMessage.content);
        
        assistantContent = isArabic ? 
          `**🏢 وكالة فيوجن للذكاء الاصطناعي - التوصية الرسمية للحملة**

بناءً على تحليل شامل من فريق الذكاء الاصطناعي، إليكم خطة الحملة المقترحة:

**📊 ملخص الحملة:**
• اسم الحملة: ${suggestion.campaignName}
• الجمهور المستهدف: ${suggestion.targetAudience}  
• استراتيجية الحملة: ${suggestion.campaignStrategy}
• المدة: ${suggestion.duration} أيام
• التكلفة الإجمالية: ${suggestion.recommendedBudget?.toLocaleString()} ريال
• التكلفة اليومية: ${suggestion.totalDailyCost?.toLocaleString()} ريال/يوم
• المواقع المختارة: ${suggestion.suggestedLocations.length} موقعاً استراتيجياً

هل تريد مني تطبيق خطة الحملة هذه على النموذج الخاص بك؟` :
          `**🏢 Fusion AI Agency - Official Campaign Recommendation**

Based on comprehensive analysis from our AI team, here's our proposed campaign plan:

**📊 Campaign Summary:**
• Campaign Name: ${suggestion.campaignName}
• Target Audience: ${suggestion.targetAudience}
• Campaign Strategy: ${suggestion.campaignStrategy}
• Duration: ${suggestion.duration} days
• Total Cost: ${suggestion.recommendedBudget?.toLocaleString()} SAR
• Daily Cost: ${suggestion.totalDailyCost?.toLocaleString()} SAR/day

**📍 Selected Locations (${suggestion.suggestedLocations.length} strategic locations):**
${suggestion.suggestedLocations.map((loc, index) => `${index + 1}. **${loc.name}** (${loc.region})
   • Media Owner: ${loc.mediaOwner}
   • Devices: ${loc.devices}
   • Daily Cost: ${loc.dailyRate?.toLocaleString()} SAR
   • Total Cost: ${loc.totalLocationCost?.toLocaleString()} SAR
   • Why this location: ${loc.reasoning}`).join('\n\n')}

**💰 Budget Breakdown:**
• Total Campaign Budget: ${suggestion.recommendedBudget?.toLocaleString()} SAR
• Campaign Duration: ${suggestion.duration} days
• Average Daily Spend: ${suggestion.totalDailyCost?.toLocaleString()} SAR/day
• Cost per Location per Day: ${Math.round((suggestion.totalDailyCost || 0) / suggestion.suggestedLocations.length).toLocaleString()} SAR

**🎯 Next Steps:**
1. Review the recommended locations and budget
2. Click "Apply Campaign Plan" to auto-fill your form
3. Upload your creative materials for each location
4. Finalize and submit your campaign

Would you like me to apply this comprehensive campaign plan to your form?`;

        assistantMessage = {
          id: Date.now().toString(),
          type: 'assistant',
          content: assistantContent,
          timestamp: new Date(),
          suggestion: suggestion,
          sender: 'Fusion AI Agency',
          role: 'agency'
        };

        setMessages(prev => [...prev, assistantMessage]);
        setCurrentSuggestion(suggestion);
      }
    } catch (error) {
      console.error('Campaign generation error:', error);
      setError('Failed to generate campaign suggestion');
      
      const errorMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: 'I apologize, but I encountered an issue while generating your campaign suggestion. Please try again with a different request.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          className="ml-auto bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200 hover:from-blue-100 hover:to-cyan-100 hover:border-blue-300 text-blue-700 hover:text-blue-800 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
        >
          <Sparkles className="h-5 w-5 mr-2" />
          AI Assistant
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="relative">
              <Bot className="h-5 w-5 text-primary" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            <span className="font-bold">AI Campaign Intelligence Network</span>
            {isTeamDiscussing && (
              <div className="flex items-center gap-1 text-sm text-secondary">
                <Users className="h-3 w-3" />
                <span className="text-xs animate-pulse">Team analyzing...</span>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-[400px] p-6 pt-2">
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`group relative max-w-[85%] ${
                      message.type === 'user' 
                        ? 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-md' 
                        : message.type === 'ai-team' 
                        ? 'bg-gradient-to-r from-secondary/20 to-accent/20 border border-secondary/30 text-foreground'
                        : 'bg-background/80 border border-border text-foreground backdrop-blur-sm'
                    } p-3 rounded-lg`}>
                      {message.type === 'ai-team' && (
                        <div className="flex items-center gap-2 mb-2 text-xs text-foreground">
                          <span className="text-sm">
                            {AI_TEAM_MEMBERS.find(member => member.name === message.sender)?.icon}
                          </span>
                          <span className="font-medium text-foreground">{message.sender}</span>
                          <span className="px-2 py-0.5 rounded-full text-xs text-foreground bg-secondary/20">
                            {AI_TEAM_MEMBERS.find(member => member.name === message.sender)?.role}
                          </span>
                        </div>
                      )}
                      
                      {(message.type === 'assistant' || message.type === 'ai-team') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleCopyMessage(message.id, message.content)}
                          className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-primary hover:text-primary/80 hover:bg-primary/10"
                        >
                          {copiedMessageId === message.id ? (
                            <CopyCheck className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                      
                      <div className={`whitespace-pre-wrap text-sm ${(message.type === 'assistant' || message.type === 'ai-team') ? 'pr-8' : ''}`}>
                        {message.content}
                      </div>
                      
                      {message.suggestion && (
                        <div className="mt-3 pt-3 border-t border-primary/20">
                          <Button
                            onClick={() => handleApplySuggestion(message.suggestion!)}
                            size="sm"
                            className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-md"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Apply This Suggestion
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {(isLoading || isTeamDiscussing) && (
                  <div className="flex justify-start">
                    <div className="bg-accent/50 border border-accent text-accent-foreground p-3 rounded-lg flex items-center gap-2 max-w-[85%]">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm">
                        {isTeamDiscussing ? 'AI team is analyzing your request...' : 'Generating optimal recommendation...'}
                      </span>
                    </div>
                  </div>
                )}
                {error && retryCount < 3 && (
                  <div className="flex justify-start">
                    <div className="bg-destructive/10 border border-destructive/30 text-destructive p-3 rounded-lg max-w-[85%]">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span className="text-sm break-words">Service temporarily unavailable</span>
                      </div>
                      <Button
                        onClick={() => setError(null)}
                        size="sm"
                        variant="outline"
                        className="w-full border-destructive/30 text-destructive hover:bg-destructive/10"
                        disabled={isLoading}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </div>

          <div className="border-t p-6 bg-background/50 border-primary/10 backdrop-blur-sm flex-shrink-0">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Describe your campaign objectives and goals..."
                disabled={isLoading || isTeamDiscussing}
                className="flex-1 text-sm bg-background/60 border-primary/20 focus:border-primary/40 backdrop-blur-sm"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading || isTeamDiscussing}
                size="icon"
                className="flex-shrink-0 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIAssistantButton;